// Toasts (rediseño, docs/PLAN-REDISENO-PORTAL.md decision 5): confirmacion
// de una accion abajo a la derecha, con la barra de 3px del tono (ok, aviso,
// error, info), que entra desde abajo y se va sola a los 7 s o con Cerrar.
// Un error se anuncia al toque (role="alert"); lo demas espera su turno
// (role="status"). Lo que requiere decision no va en un toast: sigue en la
// fila de aviso fija de cada seccion.
//
// Sin ToastProvider arriba (un componente probado solo), `useToast` avisa
// como error de cableado, igual que useOffersFeed fuera de su provider.
import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { cn } from "./cn.js";

export type ToastTone = "ok" | "aviso" | "error" | "info";

export interface Toast {
  id: number;
  tone: ToastTone;
  text: string;
}

interface ToastApi {
  notify: (text: string, tone?: ToastTone) => void;
  dismiss: (id: number) => void;
  toasts: readonly Toast[];
}

/** Cuanto queda a la vista. Suficiente para leerlo dos veces; no tapa la lista. */
export const TOAST_MS = 7_000;

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children, duration = TOAST_MS }: { children: React.ReactNode; duration?: number }): React.JSX.Element {
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const next = useRef(1);
  const dismiss = useCallback((id: number) => setToasts((list) => list.filter((t) => t.id !== id)), []);
  const notify = useCallback(
    (text: string, tone: ToastTone = "info") => {
      const id = next.current++;
      setToasts((list) => [...list, { id, tone, text }]);
      window.setTimeout(() => dismiss(id), duration);
    },
    [dismiss, duration],
  );
  const api = useMemo(() => ({ notify, dismiss, toasts }), [notify, dismiss, toasts]);
  return (
    <ToastContext.Provider value={api}>
      {children}
      <Toasts toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): Pick<ToastApi, "notify"> {
  const api = useContext(ToastContext);
  if (api === null) throw new Error("useToast fuera de ToastProvider");
  return api;
}

const BARRA: Record<ToastTone, string> = {
  ok: "border-l-ok",
  aviso: "border-l-ambar",
  error: "border-l-critico",
  info: "border-l-acero-claro",
};

function Toasts({ toasts, onDismiss }: { toasts: readonly Toast[]; onDismiss: (id: number) => void }): React.JSX.Element | null {
  if (toasts.length === 0) return null;
  return (
    <div className="fixed bottom-4 right-4 z-[80] flex w-[min(380px,calc(100vw-2rem))] flex-col gap-2">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          role={toast.tone === "error" ? "alert" : "status"}
          className={cn(
            "entrada-sube flex items-start gap-2.5 rounded-md border border-linea border-l-[3px] bg-superficie py-2.5 pl-4 pr-3 text-sm leading-normal text-texto-2 shadow-panel",
            BARRA[toast.tone],
          )}
        >
          <span className="min-w-0 flex-1">{toast.text}</span>
          <button
            type="button"
            aria-label="Cerrar aviso"
            onClick={() => onDismiss(toast.id)}
            className="shrink-0 rounded-sm px-1 text-sm leading-none text-texto-debil hover:text-texto"
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}
